import React, {useCallback, useEffect, useState} from 'react'
import {Stack, TextField, Tooltip, Typography} from '@mui/material'

import {colors} from '@/constants'
import TransCustom from '@/components/UI/i18next'
import SimpleLoader from '@/components/UI/simpleLoader'
import HoverButton from '@/components/UI/button/HoverButton'
import {useAppSelector} from '@/hooks/redux'
import {selectLanguage} from '@/store/selectors'
import {errorCodeHandler} from '@/helpers/localErrorCodeHandler'
import UserService from '@/services/api/User'
import {TransactionListItem} from '@/types/types'
import {getStockImageUrl} from '@/components/Store/MarketMaker/constants'
import {
  formatReportDate,
  getResourceDescription,
  getResourceIcon,
  getResourceName,
  toRequestDate,
} from './constants'

const ICON_SIZE = 36

const dateInputSx = {
  '& .MuiOutlinedInput-root': {
    height: '40px',
    color: colors.white,
    '& fieldset': {borderColor: colors.lightBrown},
    '&:hover fieldset': {borderColor: colors.yellow},
    '&.Mui-focused fieldset': {borderColor: colors.yellow},
  },
  '& input::-webkit-calendar-picker-indicator': {filter: 'invert(1)'},
}

const ReportRow = ({
  item,
  language,
}: {
  item: TransactionListItem
  language: string
}) => {
  const icon = getResourceIcon(item)
  const description = getResourceDescription(item, language)
  const isOut = item.amount < 0

  return (
    <Stack direction="row" alignItems="center" width="100%" gap="12px">
      <Typography variant="description" width="20%">
        {formatReportDate(item.created_at)}
      </Typography>

      <Tooltip
        arrow
        placement="top"
        title={description}
        componentsProps={{
          tooltip: {
            sx: {
              background: colors.brown,
              border: `1px solid ${colors.lightBrown}`,
              color: colors.white,
              fontSize: '13px',
              padding: '10px 12px',
              maxWidth: '320px',
            },
          },
          arrow: {sx: {color: colors.lightBrown}},
        }}>
        <Stack direction="row" alignItems="center" gap="10px" width="35%">
          {icon ? (
            <img
              src={getStockImageUrl(icon)}
              width={ICON_SIZE}
              height={ICON_SIZE}
              alt={getResourceName(item)}
              style={{objectFit: 'contain', flex: 'none'}}
            />
          ) : (
            <Stack width={`${ICON_SIZE}px`} flex="none" />
          )}
          <Typography variant="body2" color={colors.white} noWrap>
            {getResourceName(item)}
          </Typography>
        </Stack>
      </Tooltip>

      <Typography
        variant="body2"
        color={colors.gray}
        width="30%"
        sx={{wordBreak: 'break-word'}}>
        {item.description}
      </Typography>

      <Typography
        variant="body2"
        width="15%"
        textAlign="right"
        color={isOut ? colors.orange : colors.green}>
        {isOut ? '' : '+'}
        {item.amount}
      </Typography>
    </Stack>
  )
}

const Report = () => {
  const language = useAppSelector(selectLanguage)

  const [items, setItems] = useState<TransactionListItem[]>([])
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const load = useCallback(async () => {
    setIsLoading(true)
    try {
      const response = await UserService.getTransactionList(
        toRequestDate(startDate),
        toRequestDate(endDate, true)
      )
      setItems(response.data?.transactions ?? [])
    } catch (e) {
      errorCodeHandler(e)
    } finally {
      setIsLoading(false)
    }
  }, [startDate, endDate])

  useEffect(() => {
    void load()
  }, [])

  const exportCsv = () => {
    const rows = [
      ['Date', 'Resource', 'Type', 'Description', 'Amount'],
      ...items.map((item) => [
        formatReportDate(item.created_at),
        getResourceName(item),
        item.source_type,
        item.description,
        String(item.amount),
      ]),
    ]
    const csv = rows
      .map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')
      )
      .join('\n')

    const url = URL.createObjectURL(
      new Blob(['﻿' + csv], {type: 'text/csv;charset=utf-8;'})
    )
    const link = document.createElement('a')
    link.href = url
    link.download = `transactions-${startDate || 'all'}-${endDate || 'all'}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <Stack height="100%" width="100%" minHeight={0}>
      <Stack
        direction="row"
        alignItems="flex-end"
        gap="16px"
        flexWrap="wrap"
        marginTop="20px">
        <Stack gap="6px">
          <TransCustom i18nKey="1682" variant="body2" color={colors.gray} />
          <TextField
            type="date"
            size="small"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            sx={dateInputSx}
          />
        </Stack>
        <Stack gap="6px">
          <TransCustom i18nKey="1683" variant="body2" color={colors.gray} />
          <TextField
            type="date"
            size="small"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            sx={dateInputSx}
          />
        </Stack>

        <HoverButton
          onClick={load}
          disabled={isLoading}
          fontSize="15px"
          fontWeight="400"
          height="40px"
          padding="8px 22px">
          <TransCustom i18nKey="1684" fontSize="15px" />
        </HoverButton>

        <HoverButton
          onClick={exportCsv}
          disabled={isLoading || items.length === 0}
          fontSize="15px"
          fontWeight="400"
          height="40px"
          background={colors.brown}
          padding="8px 22px">
          <TransCustom i18nKey="1685" fontSize="15px" />
        </HoverButton>
      </Stack>

      <Stack direction="row" marginTop="24px" width="100%" gap="12px">
        <TransCustom i18nKey="13561" width="20%" variant="description" />
        <TransCustom i18nKey="1686" width="35%" variant="description" />
        <TransCustom i18nKey="1414" width="30%" variant="description" />
        <TransCustom
          i18nKey="1408"
          width="15%"
          variant="description"
          textAlign="right"
        />
      </Stack>

      <Stack
        flex="auto"
        minHeight={0}
        marginTop="10px"
        width="100%"
        overflow="hidden"
        borderBottom="1px solid #FFFDF04D"
        borderTop="1px solid #FFFDF04D">
        {isLoading ? (
          <Stack height="100%" padding="20px">
            <SimpleLoader isLoad={true} local={true} />
          </Stack>
        ) : items.length === 0 ? (
          <TransCustom i18nKey="1415" textAlign="center" marginTop="20px" />
        ) : (
          <Stack
            className="events-block"
            flex="auto"
            minHeight={0}
            overflow="auto"
            gap="18px"
            padding="14px 16px 14px 0"
            boxSizing="border-box"
            sx={{scrollbarGutter: 'stable'}}>
            {items.map((item, index) => (
              <ReportRow
                key={`${item.created_at}-${index}`}
                item={item}
                language={language}
              />
            ))}
          </Stack>
        )}
      </Stack>
    </Stack>
  )
}

export default Report
